<script setup>
  import { computed } from 'vue';

  const props = defineProps({
    item: Object,
    depth: Number,
    index: String,
    category: Boolean,
  });

  const component = computed(() => {
    if (props.item.url) {
      return 'RouterLink';
    } else {
      return 'span';
    }
  });

  const specific = computed(() => {
    if (props.item.url) {
      return {
        href: props.item.url || undefined,
        rel: props.item.rel || undefined,
        target: props.item.target || undefined,
        explicit: props.item.explicit,
      };
    } else {
      return {
        ['data-cc']: props.item.dataCc || undefined,
      };
    }
  });

  const level = computed(() => {
    return `level-${props.depth}`;
  });

  const children = computed(() => {
    return `nav-children-${props.depth}-${props.index}`;
  });

</script>
<template>
  <li class="nav-item" :class="level" @click="$emit('close')">
    <component
      :is="component"
      v-bind="specific"
      :class="{ category }"
      tabindex="0"
      :aria-haspopup="(category || item.children.length > 0) ? 'menu' : null"
      :aria-controls="(category || item.children.length > 0) ? children : null">
      <span>
        {{ item.name }}
        <small class="category-available" v-if="depth !== 0 && item.available">
          ({{ item.available }})
        </small>
      </span>
      <SfIconChevronRight v-if="depth === 0 && item.children.length > 0" class="nav-item-arrow" aria-hidden="true" />
    </component>
    <div v-if="category || item.children.length > 0" :id="children" class="children-wrapper">
      <ul class="children">
        <HeaderNavItem
          v-for="(item, i) in item.children"
          :item="item"
          :depth="depth + 1"
          :index="`${index}-${i}`"
          @close="$emit('close')"
        />
      </ul>
    </div>
  </li>
</template>
<style scoped lang="scss">
  .nav-item {
    .category-available {
      color: var(--color-gray);
      font-size: 0.75rem;
      font-weight: var(--font-weight-regular);
      transition: var(--primary-transition);
    }

    a {
      text-decoration: underline;
      text-decoration-color: transparent;
      transition: text-decoration-color 0.2s ease-out;
    }

    @media(hover: hover) {
      a:hover {
        text-decoration-color: var(--primary-color);
      }
    }

    &:hover, &:focus-within {
      &.level-0 > .children-wrapper {
        display: block;
        border-bottom: 1px solid var(--color-grey);
        border-right: 1px solid var(--color-grey);
      }
    }

    &.level-2 {
      font-size: 15px;

      .children-wrapper {
        display: none;
      }
    }

    &.level-0 {
      > .children-wrapper {
        position: absolute;
        display: none;
        top: 0;
        left: 299px;
        right: 0;
        height: 100%;
        padding: 1rem 1rem 0.5rem 3rem;
        background-color: var(--color-white);
        box-shadow: var(--box-shadow-menu);

        > .children {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-template-rows: auto;
          gap: 2rem 1rem;
          max-height: 100%;
          overflow-y: auto;
          overscroll-behavior: contain;
          list-style: none;
          margin: 0;

          &::-webkit-scrollbar {
            width: 8px;
          }

          &::-webkit-scrollbar-track {
            background-color: var(--color-light-background);
            border-radius: 4px;
          }

          &::-webkit-scrollbar-thumb {
            background-color: var(--primary-color);
            border-radius: 4px;
          }

          @media(max-width: 1650px) {
            grid-template-columns: repeat(3, 1fr);
          }

          @media(max-width: 1200px) {
            grid-template-columns: repeat(2, 1fr);
          }

          & > li > a {
            font-weight: var(--font-weight-semi);
            font-size: 15px;
          }
        }
      }

      > a {
        position: relative;

        &.category {
          display: flex;
          justify-content: space-between;
          width: 100%;
          padding: 0.5rem 0 0.5rem 1.25rem;
          align-items: center;
          font-weight: var(--font-weight-semi);
        }

        & svg {
          transform: translateX(-0.5rem);
          font-size: 1rem;
          transition: var(--primary-transition);
        }
      }

      @media(hover: hover) {
        &:hover {
          > a.category > svg {
            transform: translateX(0);
          }
        }
      }
    }

    a {
      display: block;
      padding: 0.25rem 0;
      transition: var(--primary-transition);

      &.active {
        color: var(--primary-color);
      }
    }

    ul {
      list-style: none;
    }
  }
</style>
